import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import TallySettingsModal from "./TallySettingsModal";
import {
  COLLECTION_TYPES,
  EmptyRow,
  LoadingOverlay,
  Modal,
  MultiSelect,
  OtpModal,
  RECEIVABLES_API,
  escapeHtml,
  fieldsFor,
  fmtDate,
  fmtDateTime,
  idList,
  money,
  sumBy,
  todayIso,
  useReceivableLookups,
  useReceivableSettings,
} from "./common";

const COLLECTIONS_API = `${RECEIVABLES_API}/collections`;
const MULTI_PENDING_STYLE = { background: "#ede9fe" };

const localDateOf = (instant) => {
  const d = new Date(instant);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const statusText = (row) => {
  if (row.bounced) return "Bounced";
  if (row.approved) return "Approved";
  if (row.approvalRequired) return "Pending";
  return fieldsFor(row.collectionType).amount ? "Posted" : "Visit";
};

/** Days from the invoice date to the day the payment was collected. */
const daysSinceInvoice = (row) => {
  if (!row.invoiceDate || !row.collectedAt) return "";
  const invoiceDay = Date.parse(`${String(row.invoiceDate).slice(0, 10)}T00:00:00Z`);
  const collectedDay = Date.parse(`${localDateOf(row.collectedAt)}T00:00:00Z`);
  return Math.max(0, Math.round((collectedDay - invoiceDay) / 86400000));
};

/**
 * Printable report of the range: every collection grouped by type (a heading and subtotal per type), then the collection and
 * denomination summaries side by side, then the customers not visited. The collection entries and the not-visited list are
 * printed only when "Include Collection Entries" / "Include No Activity Report" are on in Receivable Settings.
 */
const buildPrintHtml = ({ data, noActivity, fromDate, toDate }) => {
  const includeCollections = !!data.settings?.tenantConfig?.includeCollectionEntries;
  const includeNoActivity = !!data.settings?.tenantConfig?.includeNoActivityReport;
  const collections = data.collections || [];
  const groups = COLLECTION_TYPES.map((t) => ({ ...t, rows: collections.filter((c) => c.collectionType === t.value) })).filter(
    (g) => g.rows.length
  );
  const COLS = 14;
  const collectionBody = groups.length
    ? groups
        .map((group) => {
          const rows = group.rows
            .map(
              (row) => `<tr>
                <td>${escapeHtml(row.collectedByName || "-")}</td><td>${escapeHtml(row.customerName)}</td>
                <td>${escapeHtml(row.beatName || "-")}</td><td>${escapeHtml(row.invoiceNumber)}</td>
                <td class="c">${fmtDate(row.invoiceDate)}</td><td class="c">${escapeHtml(fmtDateTime(row.collectedAt))}</td>
                <td class="c">${daysSinceInvoice(row)}</td>
                <td class="r">${money(row.openingBalance)}</td><td class="r">${money(row.amount)}</td><td class="r">${money(row.closingBalance)}</td>
                <td>${escapeHtml(row.referenceNumber || "-")}</td><td>${escapeHtml(row.bankName || "-")}</td>
                <td class="c">${fmtDate(row.chequeDate)}</td><td class="c">${statusText(row)}</td></tr>`
            )
            .join("");
          return `<tr class="group"><td colspan="${COLS}">${escapeHtml(group.label)} (${group.rows.length})</td></tr>${rows}
            <tr class="sum"><td colspan="8">${escapeHtml(group.label)} total</td>
              <td class="r">${money(sumBy(group.rows, (r) => r.amount))}</td><td colspan="5"></td></tr>`;
        })
        .join("")
    : `<tr><td colspan="${COLS}" class="c">No collections in this range</td></tr>`;

  const totals = (data.totals || [])
    .map((row) => {
      const strong = ["OPENING BALANCE", "PAID GRAND TOTAL", "CLOSING BALANCE"].includes(row.label);
      return `<tr class="${strong ? "b" : ""}"><td>${escapeHtml(row.label)}</td><td class="c">${row.count ?? ""}</td>
        <td class="r">${money(row.amount)}</td></tr>`;
    })
    .join("");

  const denominations = data.denominations
    ? `<div><h3>Denomination Summary</h3><table><thead><tr><th>Note</th><th>Count</th><th>Total</th></tr></thead><tbody>
        ${data.denominations.rows
          .map(
            (row) => `<tr><td>${escapeHtml(row.note)}</td><td class="c">${row.note === "Coins" ? "" : row.count}</td>
              <td class="r">${money(row.total)}</td></tr>`
          )
          .join("")}
        <tr class="b"><td colspan="2">Grand Total</td><td class="r">${money(data.denominations.grandTotal)}</td></tr>
      </tbody></table></div>`
    : "";

  const notVisited = (noActivity || []).length
    ? noActivity
        .map(
          (row) => `<tr><td>${escapeHtml(row.customerName)}</td><td>${escapeHtml(row.phone || "-")}</td>
            <td>${escapeHtml(row.beatName || "-")}</td><td>${escapeHtml(row.salesmanName || "-")}</td>
            <td class="c">${row.openBills}</td><td class="r">${money(row.outstanding)}</td>
            <td class="c">${fmtDate(row.oldestInvoiceDate)}</td><td class="c">${row.maxAgeingDays}</td>
            <td>${escapeHtml(row.remarks || "-")}</td></tr>`
        )
        .join("")
    : `<tr><td colspan="9" class="c">Every customer with dues was visited</td></tr>`;

  const bills = data.bills;
  const billsSummaryRows = bills
    ? `<tr class="b"><td>TOTAL NO OF BILLS</td><td class="c">${bills.totalBills}</td><td></td></tr>
       <tr><td>Bills collected</td><td class="c">${bills.collectedBills}</td><td></td></tr>
       <tr class="b"><td>BILLS IN HAND</td><td class="c">${bills.billsInHand}</td><td class="r">${money(bills.balanceInHand)}</td></tr>`
    : "";
  const billsInHandTable =
    bills && bills.inHand.length
      ? `<h3>Bills in Hand (${bills.inHand.length})</h3><table><thead><tr><th>Invoice</th><th>Inv. Date</th><th>Customer</th><th>Beat</th><th>Salesman</th>
          <th>Status</th><th>Invoice Amt</th><th>Collected</th><th>Balance</th><th>Last Entry</th></tr></thead><tbody>${bills.inHand
          .map(
            (b) => `<tr><td>${escapeHtml(b.invoiceNumber)}</td><td class="c">${fmtDate(b.invoiceDate)}</td><td>${escapeHtml(b.customerName)}</td>
              <td>${escapeHtml(b.beatName || "-")}</td><td>${escapeHtml(b.salesmanName || "-")}</td><td>${escapeHtml(b.statusLabel)}</td>
              <td class="r">${money(b.invoiceAmount)}</td><td class="r">${money(b.collectedAmount)}</td><td class="r">${money(b.balance)}</td>
              <td>${b.lastActivityAt ? escapeHtml(`${fmtDateTime(b.lastActivityAt)} ${b.lastActivityBy || ""}`) : "-"}</td></tr>`
          )
          .join("")}</tbody></table>`
      : "";
  const range = fromDate === toDate ? fmtDate(fromDate) : `${fmtDate(fromDate)} to ${fmtDate(toDate)}`;
  return `<!doctype html><html><head><title>Collections ${escapeHtml(range)}</title><style>
    @page { size: A4 portrait; margin: 8mm; }
    body { font-family: Helvetica, Arial, sans-serif; margin: 0; color: #000; }
    h1 { font-size: 15px; text-align: center; margin: 2px 0 8px; }
    h3 { font-size: 12px; margin: 14px 0 4px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #000; font-size: 7.5px; padding: 2px 3px; vertical-align: top; overflow-wrap: anywhere; }
    th { background: #eee; font-size: 7.5px; }
    thead { display: table-header-group; }
    tr { page-break-inside: avoid; }
    tr.group td { background: #e5e7eb; font-weight: bold; font-size: 9px; }
    tr.sum td, tr.b td { font-weight: bold; }
    .c { text-align: center; } .r { text-align: right; white-space: nowrap; }
    .side { display: flex; gap: 16px; align-items: flex-start; page-break-inside: avoid; }
    .side > div { flex: 1; }
  </style></head><body>
    <h1>COLLECTIONS — ${escapeHtml(range)}</h1>
    ${
      includeCollections
        ? `<table>
      <thead><tr><th>Salesperson</th><th>Customer</th><th>Beat</th><th>Invoice</th><th>Inv. Date</th><th>Collected</th><th>Days</th>
        <th>Opening</th><th>Amount</th><th>Closing</th><th>Ref No (Chq / UTR)</th><th>Bank</th><th>Chq/Txn Date</th><th>Status</th></tr></thead>
      <tbody>${collectionBody}</tbody>
    </table>`
        : ""
    }
    <div class="side">
      <div><h3>Collection Summary</h3><table><thead><tr><th>Collection Type</th><th>Payments</th><th>Amount</th></tr></thead>
        <tbody>${totals}${billsSummaryRows}</tbody></table></div>
      ${denominations}
    </div>
    ${billsInHandTable}
    ${
      includeNoActivity
        ? `<h3>Not Visited</h3>
    <table>
      <thead><tr><th>Customer</th><th>Phone</th><th>Beat</th><th>Salesman</th><th>Open Bills</th><th>Outstanding</th><th>Oldest Bill</th>
        <th>Ageing</th><th>Remarks</th></tr></thead>
      <tbody>${notVisited}</tbody>
    </table>`
        : ""
    }
  </body></html>`;
};

const statusBadge = (row) => {
  if (row.bounced) return <span className="badge badge-red">Bounced</span>;
  if (row.approved) return <span className="badge badge-green">Approved</span>;
  if (row.approvalRequired) return <span className="badge badge-amber">Pending</span>;
  if (fieldsFor(row.collectionType).amount) return <span className="badge badge-blue">Posted</span>;
  return <span className="badge badge-gray">Visit</span>;
};

const EditCollectionModal = ({ row, onClose, onSaved }) => {
  const { banks } = useReceivableLookups(["banks"]);
  const [form, setForm] = useState({
    collectionType: row.collectionType,
    amount: row.amount ?? "",
    referenceNumber: row.referenceNumber || "",
    bankName: row.bankName || "",
    branchName: row.branchName || "",
    chequeDate: row.chequeDate || "",
    paymentDate: row.collectedAt ? localDateOf(row.collectedAt) : todayIso(),
  });
  const [saving, setSaving] = useState(false);
  const f = fieldsFor(form.collectionType);
  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`${COLLECTIONS_API}/${row.id}`, {
        collectionType: form.collectionType,
        amount: f.amount ? Number(form.amount) : 0,
        // a credit note's reference is not edited here, so the one it has is kept
        referenceNumber: f.reference || form.collectionType === "CREDIT_NOTE" ? form.referenceNumber.trim() || null : null,
        bankName: f.bank ? form.bankName.trim() || null : null,
        branchName: f.branch ? form.branchName.trim() || null : null,
        chequeDate: f.instrumentDate ? form.chequeDate || null : null,
        paymentDate: form.paymentDate || null,
      });
      toast.success("Collection updated");
      onSaved();
    } catch (error) {
      toast.error(error.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`Edit ${row.receiptNumber || "collection"} — ${row.invoiceNumber}`} onClose={onClose}>
      <form onSubmit={save}>
        <div className="page-subtitle">
          {row.customerName} · invoice balance ₹{money(row.invoiceBalance)}
        </div>
        <div className="form-grid">
          <div className="form-group">
            <label>Type</label>
            <select value={form.collectionType} onChange={(e) => set("collectionType", e.target.value)}>
              {COLLECTION_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          {f.amount ? (
            <div className="form-group">
              <label>Amount</label>
              <input type="number" min="0.01" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)} required />
            </div>
          ) : null}
          {f.reference ? (
            <div className="form-group">
              <label>{f.referenceLabel}</label>
              <input value={form.referenceNumber} onChange={(e) => set("referenceNumber", e.target.value)} />
            </div>
          ) : null}
          {f.instrumentDate ? (
            <div className="form-group">
              <label>{f.instrumentDateLabel}</label>
              <input type="date" value={form.chequeDate} onChange={(e) => set("chequeDate", e.target.value)} />
            </div>
          ) : null}
          {f.bank ? (
            <div className="form-group">
              <label>Bank</label>
              <select value={form.bankName} onChange={(e) => set("bankName", e.target.value)}>
                <option value="">Select bank</option>
                {/* a bank saved before the bank master existed stays selectable on its own row */}
                {form.bankName && !banks.includes(form.bankName) ? <option value={form.bankName}>{form.bankName}</option> : null}
                {banks.map((bank) => (
                  <option key={bank} value={bank}>
                    {bank}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          {f.branch ? (
            <div className="form-group">
              <label>Branch</label>
              <input value={form.branchName} onChange={(e) => set("branchName", e.target.value)} />
            </div>
          ) : null}
          <div className="form-group">
            <label>Payment Date</label>
            <input type="date" value={form.paymentDate} onChange={(e) => set("paymentDate", e.target.value)} />
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

/** Loads a Shop Close entry's photo (it needs the session token, so it cannot be a plain img src) as an object URL. */
const useShopPhoto = (collectionId, thumbnail = false) => {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    setUrl(null);
    setError(null);
    api
      .fetchBlob(`${COLLECTIONS_API}/${collectionId}/shop-image${thumbnail ? "?thumbnail=true" : ""}`)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Could not load the photo");
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [collectionId, thumbnail]);

  return { url, error };
};

/** Small photo in the Shop Image column; a click opens it full size (legacy pdc_details "Shop Image"). */
const ShopPhotoThumb = ({ row, onOpen }) => {
  const { url, error } = useShopPhoto(row.id, true);
  if (error) return <span title={error}>Unavailable</span>;
  if (!url) return <span className="page-subtitle">Loading...</span>;
  return (
    <img
      src={url}
      alt={`Closed shop of ${row.customerName}`}
      title="View photo"
      onClick={() => onOpen(row)}
      style={{ display: "block", margin: "0 auto", maxWidth: 50, maxHeight: 50, cursor: "pointer", borderRadius: 4 }}
    />
  );
};

/** The photo the field user took of the closed shop, full size. */
const ShopPhotoModal = ({ row, onClose }) => {
  const { url, error } = useShopPhoto(row.id);

  return (
    <Modal title={`Shop photo — ${row.customerName}`} onClose={onClose}>
      <div className="page-subtitle">
        {row.collectedByName || "-"} · {fmtDateTime(row.collectedAt)} · {row.invoiceNumber}
      </div>
      <div style={{ textAlign: "center", minHeight: 120 }}>
        {error ? (
          <span style={{ color: "var(--color-danger)" }}>{error}</span>
        ) : url ? (
          <img src={url} alt={`Closed shop of ${row.customerName}`} style={{ display: "block", margin: "0 auto", maxWidth: "100%", maxHeight: "70vh", borderRadius: 6 }} />
        ) : (
          <span className="page-subtitle">Loading photo...</span>
        )}
      </div>
    </Modal>
  );
};

// Why a row cannot be picked for approval — shown as the disabled checkbox's tooltip.
const notSelectableReason = (row) => {
  if (row.bounced) return "Bounced cheque";
  if (row.approved) return "Already approved";
  if (!fieldsFor(row.collectionType).amount) return "Visit entry — nothing to approve";
  return "Posted directly — approval is not switched on for this type in Admin → Settings";
};

const CollectionTable = ({ rows, selected, onToggle, onToggleAll, onEdit, onDelete, onViewPhoto, showPhoto }) => {
  const selectable = rows.filter((row) => row.approvalRequired && !row.approved && !row.bounced);
  const allSelected = selectable.length > 0 && selectable.every((row) => selected.has(row.id));
  const hasInstrument = rows.some((row) => row.referenceNumber || row.bankName || row.chequeDate);
  return (
    <div className="table-wrapper" style={{ maxHeight: "55vh", overflowY: "auto" }}>
      <table>
        <thead>
          <tr>
            <th>
              <input type="checkbox" checked={allSelected} disabled={!selectable.length} onChange={() => onToggleAll(selectable, !allSelected)} aria-label="Select all" />
            </th>
            <th>Salesperson</th>
            <th>Customer</th>
            <th>Beat</th>
            <th>Invoice</th>
            <th>Collected</th>
            <th style={{ textAlign: "right" }}>Opening</th>
            <th style={{ textAlign: "right" }}>Amount</th>
            <th style={{ textAlign: "right" }}>Closing</th>
            {hasInstrument ? (
              <>
                <th>Reference</th>
                <th>Bank</th>
                <th>Chq/Txn Date</th>
              </>
            ) : null}
            {showPhoto ? <th style={{ textAlign: "center" }}>Shop Image</th> : null}
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <EmptyRow colSpan={(hasInstrument ? 14 : 11) + (showPhoto ? 1 : 0)} title="Nothing collected of this type" />
          ) : (
            rows.map((row) => {
              const pending = row.approvalRequired && !row.approved && !row.bounced;
              return (
                <tr key={row.id} style={row.multiplePendingOnInvoice ? MULTI_PENDING_STYLE : undefined}>
                  <td>
                    <input
                      type="checkbox"
                      checked={pending && selected.has(row.id)}
                      disabled={!pending}
                      onChange={() => onToggle(row.id)}
                      title={pending ? "Select for approval" : notSelectableReason(row)}
                      aria-label={`Select ${row.invoiceNumber}`}
                    />
                  </td>
                  <td>{row.collectedByName || "-"}</td>
                  <td title={row.customerName}>{row.customerName}</td>
                  <td>{row.beatName || "-"}</td>
                  <td>{row.invoiceNumber}</td>
                  <td>{fmtDateTime(row.collectedAt)}</td>
                  <td style={{ textAlign: "right" }}>{money(row.openingBalance)}</td>
                  <td style={{ textAlign: "right" }}>{money(row.amount)}</td>
                  <td style={{ textAlign: "right" }}>{money(row.closingBalance)}</td>
                  {hasInstrument ? (
                    <>
                      <td>{row.referenceNumber || "-"}</td>
                      <td title={row.branchName || ""}>{row.bankName || "-"}</td>
                      <td>{fmtDate(row.chequeDate)}</td>
                    </>
                  ) : null}
                  {showPhoto ? (
                    <td style={{ textAlign: "center" }}>{row.hasShopImage ? <ShopPhotoThumb row={row} onOpen={onViewPhoto} /> : "-"}</td>
                  ) : null}
                  <td>{statusBadge(row)}</td>
                  <td className="action-buttons flex gap-2">
                    <button className="secondary" onClick={() => onEdit(row)} disabled={row.approved || row.bounced}>
                      Edit
                    </button>
                    <button className="secondary" onClick={() => onDelete(row)}>
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
        {rows.length ? (
          <tfoot>
            <tr>
              <td colSpan={7} style={{ fontWeight: 700 }}>
                Count: {rows.length}
              </td>
              <td style={{ fontWeight: 700, textAlign: "right" }}>{money(sumBy(rows, (r) => r.amount))}</td>
              <td colSpan={(hasInstrument ? 6 : 3) + (showPhoto ? 1 : 0)} />
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
};

/** Collections in a date range by payment type, with OTP approval and edit/delete (legacy pdc_details.aspx). */
const CollectionsDashboard = () => {
  const { salesmen, beats } = useReceivableLookups(["salesmen", "beats"]);
  const [fromDate, setFromDate] = useState(todayIso());
  const [toDate, setToDate] = useState(todayIso());
  const [salesmanIds, setSalesmanIds] = useState([]);
  const [beatIds, setBeatIds] = useState([]);
  const [loadSheetNumber, setLoadSheetNumber] = useState("");
  const [data, setData] = useState(null);
  const [noActivity, setNoActivity] = useState(null);
  const [tab, setTab] = useState("CASH");
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const [photoRow, setPhotoRow] = useState(null);
  const [tallySettingsOpen, setTallySettingsOpen] = useState(false);
  const [pendingOtp, setPendingOtp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadedParams, setLoadedParams] = useState(null);
  const receivableSettings = useReceivableSettings();
  const dayBeatAssigned = !!receivableSettings?.tenantConfig?.dayBeatAssigned;
  const [beatDays, setBeatDays] = useState([]);

  useEffect(() => {
    if (!dayBeatAssigned) return;
    api
      .get(`${RECEIVABLES_API}/beat-day-assignments`)
      .then((rows) => setBeatDays(Array.isArray(rows) ? rows : []))
      .catch(() => setBeatDays([]));
  }, [dayBeatAssigned]);

  const params = () => ({ fromDate, toDate, salesmanUserIds: idList(salesmanIds), beatIds: idList(beatIds), loadSheetNumber });

  const run = async (work) => {
    setLoading(true);
    try {
      await work();
    } catch (error) {
      toast.error(error.message || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const load = () =>
    run(async () => {
      if (fromDate > toDate) throw new Error("From date cannot be after to date");
      setData(await api.get(COLLECTIONS_API, params()));
      setLoadedParams(params());
      setSelected(new Set());
      if (tab === "NO_ACTIVITY") setNoActivity(await api.get(`${COLLECTIONS_API}/no-activity`, params()));
    });

  const openNoActivity = () =>
    run(async () => {
      setTab("NO_ACTIVITY");
      setNoActivity(await api.get(`${COLLECTIONS_API}/no-activity`, params()));
    });

  const requestApproval = () =>
    run(async () => {
      // Only the selection on the visible tab is sent, matching the count on the button.
      if (pendingInTab.length === 0) throw new Error("Select at least one pending collection");
      setPendingOtp(await api.post(`${COLLECTIONS_API}/approval-requests`, { ids: pendingInTab.map((row) => row.id) }));
    });

  const remove = (row) => {
    const note = row.balanceApplied ? " Its amount will be reversed on the invoice." : "";
    if (!window.confirm(`Delete ${row.collectionTypeLabel} ₹${money(row.amount)} on ${row.invoiceNumber}?${note}`)) return;
    run(async () => {
      const result = await api.del(`${COLLECTIONS_API}/${row.id}`);
      toast.success(result?.message || "Collection deleted");
      setData(await api.get(COLLECTIONS_API, params()));
    });
  };

  const download = (path, filename) => run(() => api.downloadFile(path, params(), filename));

  // The window is opened before the not-visited list is fetched so the browser does not block it as a pop-up.
  const print = () => {
    const win = window.open("", "_blank");
    if (!win) {
      toast.error("Allow pop-ups to print the collections report");
      return;
    }
    win.document.write("<p style='font-family:sans-serif'>Preparing report...</p>");
    run(async () => {
      try {
        // Printed for the filters the shown data was generated with, even if they were changed since.
        const notVisited = data.settings?.tenantConfig?.includeNoActivityReport
          ? await api.get(`${COLLECTIONS_API}/no-activity`, loadedParams)
          : [];
        win.document.open();
        win.document.write(buildPrintHtml({ data, noActivity: notVisited, fromDate: loadedParams.fromDate, toDate: loadedParams.toDate }));
        win.document.close();
        win.focus();
        setTimeout(() => win.print(), 300);
      } catch (error) {
        win.close();
        throw error;
      }
    });
  };

  const collections = data?.collections || [];
  const counts = COLLECTION_TYPES.map((t) => ({ ...t, count: collections.filter((c) => c.collectionType === t.value).length }));
  const tabRows = collections.filter((c) => c.collectionType === tab);
  const approvableInTab = tabRows.filter((row) => row.approvalRequired && !row.approved && !row.bounced);
  const pendingInTab = approvableInTab.filter((row) => selected.has(row.id));

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = (rows, on) =>
    setSelected((prev) => {
      const next = new Set(prev);
      rows.forEach((row) => (on ? next.add(row.id) : next.delete(row.id)));
      return next;
    });

  // legacy IsDayBeatEnabled: only the beats worked on the chosen day are offered, and none for a range of dates
  const visitDay = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"][new Date(`${fromDate}T00:00:00`).getDay()];
  const dayBeatIds = new Set(beatDays.filter((row) => row.visitDay === visitDay).map((row) => row.beatId));
  const beatOptions = beats
    .filter((b) => !dayBeatAssigned || (fromDate === toDate && dayBeatIds.has(b.id)))
    .filter((b) => salesmanIds.length === 0 || salesmanIds.includes(b.salesmanUserId))
    .map((b) => ({ value: b.id, label: b.name }));

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <LoadingOverlay show={loading} />
      <div className="filters-bar">
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} aria-label="From date" />
        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} aria-label="To date" />
        <MultiSelect
          options={salesmen.map((s) => ({ value: s.userId, label: s.name }))}
          selected={salesmanIds}
          onChange={(ids) => {
            setSalesmanIds(ids);
            setBeatIds([]);
          }}
          placeholder="All salespersons"
        />
        <MultiSelect options={beatOptions} selected={beatIds} onChange={setBeatIds} placeholder="All beats" />
        {data?.settings?.loadSheetNumberRequired ? (
          <input type="text" value={loadSheetNumber} onChange={(e) => setLoadSheetNumber(e.target.value)} placeholder="Load sheet no" />
        ) : null}
        <button className="primary" onClick={load}>
          Generate
        </button>
        <button
          className="primary"
          onClick={print}
          disabled={!data}
          style={{ background: "#f59e0b", borderColor: "#f59e0b", color: "#1f2937", fontWeight: 700 }}
        >
          Print
        </button>
        <button className="secondary" onClick={() => download(`${COLLECTIONS_API}/export`, `collections-${fromDate}.xlsx`)} disabled={!data}>
          Export
        </button>
        <button
          className="secondary"
          onClick={() => download(`${COLLECTIONS_API}/tally/export`, `Tally_Input_Collections_${fromDate}.xlsx`)}
          disabled={!data}
          title="Collections in the Tally input layout (columns set in Tally Settings)"
        >
          Export for Tally
        </button>
        {receivableSettings?.tallySettingsEnabled ? (
          <button className="secondary" onClick={() => setTallySettingsOpen(true)} title="Columns, voucher types and ledgers of the Tally export">
            Tally Settings
          </button>
        ) : null}
        {data?.settings?.tenantConfig?.importExcelEnabled ? (
          <button className="secondary" onClick={() => download(`${COLLECTIONS_API}/import-excel`, `ITC_Import_Excel_${fromDate}.xlsx`)}>
            Import Excel
          </button>
        ) : null}
      </div>

      {data ? (
        <>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 16 }}>
            {counts.map((t) => (
              <button key={t.value} className={tab === t.value ? "primary" : "secondary"} onClick={() => setTab(t.value)}>
                {t.label} ({t.count})
              </button>
            ))}
            <button className={tab === "NO_ACTIVITY" ? "primary" : "secondary"} onClick={openNoActivity}>
              No Activity{noActivity ? ` (${noActivity.length})` : ""}
            </button>
          </div>

          {tab === "NO_ACTIVITY" ? (
            <>
              <div className="filters-bar" style={{ marginTop: 12 }}>
                <span className="page-subtitle">
                  Customers on the selected beats who owe money but had no cash / UPI / cheque / e-fund / no-payment visit in this range.
                </span>
                <button className="secondary" onClick={() => download(`${COLLECTIONS_API}/no-activity/export`, `no-activity-${fromDate}.xlsx`)}>
                  Export
                </button>
              </div>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Phone</th>
                      <th>Beat</th>
                      <th>Salesman</th>
                      <th style={{ textAlign: "center" }}>Open Bills</th>
                      <th style={{ textAlign: "right" }}>Outstanding</th>
                      <th>Oldest Bill</th>
                      <th style={{ textAlign: "center" }}>Ageing</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!noActivity?.length ? (
                      <EmptyRow colSpan={9} title="Every customer with dues was visited" />
                    ) : (
                      noActivity.map((row) => (
                        <tr key={row.customerId} style={row.remarks ? { background: "#fef2f2" } : undefined}>
                          <td title={row.customerName}>{row.customerName}</td>
                          <td>{row.phone || "-"}</td>
                          <td>{row.beatName || "-"}</td>
                          <td>{row.salesmanName || "-"}</td>
                          <td style={{ textAlign: "center" }}>{row.openBills}</td>
                          <td style={{ textAlign: "right" }}>{money(row.outstanding)}</td>
                          <td>{fmtDate(row.oldestInvoiceDate)}</td>
                          <td style={{ textAlign: "center" }}>{row.maxAgeingDays}</td>
                          <td>{row.remarks || "-"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <>
              <div className="filters-bar" style={{ marginTop: 12 }}>
                <button className="primary" onClick={requestApproval} disabled={pendingInTab.length === 0}>
                  Approve selected ({pendingInTab.length}) — ₹{money(sumBy(pendingInTab, (r) => r.amount))}
                </button>
                <span className="page-subtitle">
                  {approvableInTab.length
                    ? `${approvableInTab.length} waiting for approval. Purple rows: more than one payment waiting for approval on the same bill — opening/closing are projected in order.`
                    : "Nothing of this type is waiting for approval — collections are posted directly unless approval is switched on for the type in Admin → Settings."}
                </span>
              </div>
              <CollectionTable
                rows={tabRows}
                selected={selected}
                onToggle={toggle}
                onToggleAll={toggleAll}
                onEdit={setEditing}
                onDelete={remove}
                onViewPhoto={setPhotoRow}
                showPhoto={tab === "SHOP_CLOSE"}
              />
            </>
          )}
          <h3 style={{ margin: "20px 0 0", fontWeight: 600 }}>Salesperson Summary</h3>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Salesperson</th>
                  <th style={{ textAlign: "right" }}>Cash</th>
                  <th style={{ textAlign: "right" }}>UPI</th>
                  <th style={{ textAlign: "right" }}>Cheque</th>
                  <th style={{ textAlign: "right" }}>E-Fund</th>
                  <th style={{ textAlign: "right" }}>Credit Note</th>
                  <th style={{ textAlign: "right" }}>Debit Note</th>
                  <th style={{ textAlign: "right" }}>Expired</th>
                  <th style={{ textAlign: "right" }}>FC Payout</th>
                  <th style={{ textAlign: "right" }}>Total</th>
                  <th style={{ textAlign: "right" }}>Cash Handed Over</th>
                </tr>
              </thead>
              <tbody>
                {data.salespersons.length === 0 ? (
                  <EmptyRow colSpan={11} title="No collections in this range" />
                ) : (
                  data.salespersons.map((row) => (
                    <tr key={row.userId ?? "none"}>
                      <td>{row.name}</td>
                      <td style={{ textAlign: "right" }}>{money(row.cash)} ({row.cashCount})</td>
                      <td style={{ textAlign: "right" }}>{money(row.upi)} ({row.upiCount})</td>
                      <td style={{ textAlign: "right" }}>{money(row.cheque)} ({row.chequeCount})</td>
                      <td style={{ textAlign: "right" }}>{money(row.efund)} ({row.efundCount})</td>
                      <td style={{ textAlign: "right" }}>{money(row.credit)} ({row.creditCount})</td>
                      <td style={{ textAlign: "right" }}>{money(row.debit)} ({row.debitCount})</td>
                      <td style={{ textAlign: "right" }}>{money(row.expired)} ({row.expiredCount})</td>
                      <td style={{ textAlign: "right" }}>{money(row.fcPayout)} ({row.fcPayoutCount})</td>
                      <td style={{ textAlign: "right", fontWeight: 700 }}>
                        {money(row.total)} ({row.totalCount})
                      </td>
                      <td
                        style={{
                          textAlign: "right",
                          color:
                            row.denominationTotal !== null && row.denominationTotal !== undefined && Number(row.denominationTotal) < Number(row.cash)
                              ? "var(--color-danger)"
                              : undefined,
                        }}
                      >
                        {money(row.denominationTotal)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16, marginTop: 20, alignItems: "start" }}>
            <div className="table-wrapper" style={{ marginTop: 0 }}>
              <table>
                <thead>
                  <tr>
                    <th>Collection Type</th>
                    <th style={{ textAlign: "center" }}>Payments</th>
                    <th style={{ textAlign: "right" }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.totals.map((row) => {
                    const strong = ["OPENING BALANCE", "PAID GRAND TOTAL", "CLOSING BALANCE"].includes(row.label);
                    return (
                      <tr key={row.label} style={strong ? { fontWeight: 700, background: "#fafafc" } : undefined}>
                        <td>{row.label}</td>
                        <td style={{ textAlign: "center" }}>{row.count ?? ""}</td>
                        <td style={{ textAlign: "right" }}>{money(row.amount)}</td>
                      </tr>
                    );
                  })}
                  {data.bills ? (
                    <>
                      <tr style={{ fontWeight: 700, background: "#fafafc" }}>
                        <td>TOTAL NO OF BILLS</td>
                        <td style={{ textAlign: "center" }}>{data.bills.totalBills}</td>
                        <td />
                      </tr>
                      <tr>
                        <td>Bills collected (fully paid)</td>
                        <td style={{ textAlign: "center" }}>{data.bills.collectedBills}</td>
                        <td />
                      </tr>
                      <tr style={{ fontWeight: 700, background: "#fafafc" }}>
                        <td title="Part payment, shop close, no payment and not visited">BILLS IN HAND</td>
                        <td style={{ textAlign: "center" }}>{data.bills.billsInHand}</td>
                        <td style={{ textAlign: "right" }}>{money(data.bills.balanceInHand)}</td>
                      </tr>
                      <tr>
                        <td style={{ paddingLeft: 22 }}>Part payment · Shop close · No payment · Not visited</td>
                        <td style={{ textAlign: "center" }} colSpan={2}>
                          {data.bills.partPayment} · {data.bills.shopClose} · {data.bills.noPayment} · {data.bills.notVisited}
                        </td>
                      </tr>
                    </>
                  ) : null}
                </tbody>
              </table>
            </div>
            {data.denominations ? (
              <div className="table-wrapper" style={{ marginTop: 0 }}>
                <table>
                  <thead>
                    <tr>
                      <th>Note</th>
                      <th style={{ textAlign: "center" }}>Count</th>
                      <th style={{ textAlign: "right" }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.denominations.rows.map((row) => (
                      <tr key={row.note}>
                        <td>{row.note}</td>
                        <td style={{ textAlign: "center" }}>{row.note === "Coins" ? "" : row.count}</td>
                        <td style={{ textAlign: "right" }}>{money(row.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2} style={{ fontWeight: 700 }}>
                        Grand Total
                        {data.denominations.dayClosed === true ? <span className="badge badge-green" style={{ marginLeft: 8 }}>Day closed</span> : null}
                        {data.denominations.dayClosed === false ? <span className="badge badge-amber" style={{ marginLeft: 8 }}>Day open</span> : null}
                      </td>
                      <td style={{ fontWeight: 700, textAlign: "right" }}>{money(data.denominations.grandTotal)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : null}
          </div>

          {data.bills ? (
            <>
              <h3 style={{ margin: "20px 0 0", fontWeight: 600 }}>Bills in hand ({data.bills.inHand.length})</h3>
              <div className="page-subtitle">
                Bills part paid, with the shop closed, visited without payment, or not visited in the period. Open bills of the beats / salesmen /
                load sheet picked are included; with no such filter only bills with an entry in the period are counted.
              </div>
              <div className="table-wrapper" style={{ maxHeight: "50vh", overflowY: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th>Invoice</th>
                      <th>Inv. Date</th>
                      <th>Customer</th>
                      <th>Beat</th>
                      <th>Salesman</th>
                      <th>Status</th>
                      <th style={{ textAlign: "right" }}>Invoice Amt</th>
                      <th style={{ textAlign: "right" }}>Collected</th>
                      <th style={{ textAlign: "right" }}>Balance</th>
                      <th>Last Entry</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.bills.inHand.length === 0 ? (
                      <EmptyRow colSpan={10} title="No bills in hand" />
                    ) : (
                      data.bills.inHand.map((b) => (
                        <tr key={b.invoiceId}>
                          <td>{b.invoiceNumber}</td>
                          <td>{fmtDate(b.invoiceDate)}</td>
                          <td>{b.customerName}</td>
                          <td>{b.beatName || "-"}</td>
                          <td>{b.salesmanName || "-"}</td>
                          <td>
                            <span
                              className={`badge ${
                                b.status === "PART_PAYMENT" ? "badge-amber" : b.status === "NOT_VISITED" ? "badge-red" : "badge-gray"
                              }`}
                            >
                              {b.statusLabel}
                            </span>
                          </td>
                          <td style={{ textAlign: "right" }}>{money(b.invoiceAmount)}</td>
                          <td style={{ textAlign: "right" }}>{money(b.collectedAmount)}</td>
                          <td style={{ textAlign: "right", fontWeight: 600 }}>{money(b.balance)}</td>
                          <td>{b.lastActivityAt ? `${fmtDateTime(b.lastActivityAt)} ${b.lastActivityBy || ""}` : "-"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </>
      ) : (
        <div className="page-subtitle" style={{ marginTop: 16 }}>
          Choose a date range and press Generate.
        </div>
      )}

      {editing ? (
        <EditCollectionModal
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      ) : null}

      {photoRow ? <ShopPhotoModal row={photoRow} onClose={() => setPhotoRow(null)} /> : null}
      {tallySettingsOpen ? <TallySettingsModal onClose={() => setTallySettingsOpen(false)} /> : null}

      <OtpModal
        pending={pendingOtp}
        verifyPath={(id) => `${COLLECTIONS_API}/approval-requests/${id}/verify`}
        onClose={() => setPendingOtp(null)}
        onVerified={() => {
          setPendingOtp(null);
          load();
        }}
      />
    </div>
  );
};

export default CollectionsDashboard;
